/**
 * DrawerTaskPanel — 任务列表状态管理 hook
 *
 * 公共 hook，被素材库侧边栏（taskPanel）、library 各功能 Tab、爆款模板「我的创作」共用。
 *
 * 核心能力：
 * - 分页加载：loadTaskList / loadMore，按 pageSize 分页拉取 creationDetailList
 * - 可视页码轮询：通过 setVisiblePages 接收当前视口内的虚拟页码，
 *   仅对包含 pending 子任务的 batch 所在的 API 页发起轮询（15s 间隔，300ms 防抖）
 * - CRUD：deleteTask（整批删除）、deleteMediaItem（单个素材删除）、updateMediaItemTitle（重命名）
 * - 数据转换：CreationDetail → CombinedTaskItem（通过 taskListTransforms）
 *
 * 轮询机制说明：
 * - batchPollPageRef 记录每个 batch（taskId）来自哪个 API 页码
 * - 可视页码变化时，找出视口内有 pending 子任务的 batch → 查 batchPollPageRef 得到 API 页码 → 重新请求该页
 * - 用 patchTasksAfterPoll 按 taskId 匹配，原地更新 state/mediaItems/rawData
 */
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { deleteBatch } from '@/services/subtitleTranslation';
import UserStore from '@/store/User';
import { F_CLOUD_TASK_TYPE } from '@/utils/const';
import { t } from '@/utils/helper/intl';

import {
  type AssetItem,
  type CreationDetail,
  fetchTaskList,
  type QueryType,
  type TaskItem,
  type TaskListParams,
  TaskType,
} from './service';
import { transformCreationDetailToCombined } from './taskListTransforms';

export enum MediaType {
  VIDEO = 3,
  IMAGE = 2,
  AUDIO = 5,
  PENDING = 0,
}

export enum TaskState {
  PENDING = 1,
  SUCCESS = 2,
  FAILED = 3,
  NonCompliance = 5,
}

export interface MediaItem {
  id: number;
  title: string;
  url: string;
  cover: string;
  duration?: number;
  state: TaskState;
  mediaType: MediaType;
  rawData: AssetItem | TaskItem;
}

export interface CombinedTaskItem {
  id: number;
  taskId?: number;
  title: string;
  tags?: string[];
  createTime: number;
  cloudTaskType: F_CLOUD_TASK_TYPE;
  state: TaskState;
  mediaItems: MediaItem[];
  prompt?: string;
  promptImage?: string | string[];
  rawData: CreationDetail;
  modelName?: string;
}

interface UseTaskListOptions {
  queryTypes: QueryType[];
  pageSize?: number;
  batchPollingInterval?: number;
  pathname?: string;
  active?: boolean;
}

export const DEFAULT_PAGE_SIZE = 40;
const DEFAULT_BATCH_POLLING_INTERVAL = 15000;
const PAGE_SWITCH_DEBOUNCE = 300;

function sortCombinedByTimeDesc(a: CombinedTaskItem, b: CombinedTaskItem) {
  return b.createTime - a.createTime;
}

function batchHasPendingSubTasks(batch: CombinedTaskItem) {
  const taskListFromRaw = batch.rawData?.taskList || [];
  return taskListFromRaw.some((t) => t.state === TaskState.PENDING);
}

function patchTasksAfterPoll(
  prev: CombinedTaskItem[],
  newTaskMap: Map<number, CombinedTaskItem>,
) {
  return prev.map((oldTask) => {
    const tid = (oldTask.taskId ?? oldTask.id) as number;
    const newTask = newTaskMap.get(tid);
    if (newTask) {
      return {
        ...oldTask,
        state: newTask.state,
        mediaItems: newTask.mediaItems,
        rawData: newTask.rawData,
      };
    }
    return oldTask;
  });
}

export const useTaskList = ({
  queryTypes,
  pageSize = DEFAULT_PAGE_SIZE,
  batchPollingInterval = DEFAULT_BATCH_POLLING_INTERVAL,
  pathname,
  active = true,
}: UseTaskListOptions) => {
  const buildListRequestPayload = useCallback(
    (page: number): TaskListParams => ({
      queryTypes,
      pageNum: page,
      pageSize,
    }),
    [queryTypes, pageSize],
  );

  const [taskList, setTaskList] = useState<CombinedTaskItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [visiblePages, setVisiblePagesState] = useState<Set<number>>(new Set([1]));

  const pollingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const loadingRef = useRef(false);
  const visiblePagesRef = useRef<Set<number>>(new Set([1]));
  const taskListRef = useRef<CombinedTaskItem[]>([]);
  /** 记录每个 batch 来自哪个 API 页，轮询时按页重新请求 */
  const batchPollPageRef = useRef<Map<number, number>>(new Map());

  taskListRef.current = taskList;

  const transformDetailToCombinedItem = useCallback(
    (detail: CreationDetail, fallbackId: string | number): CombinedTaskItem =>
      transformCreationDetailToCombined(detail, pathname, Number(fallbackId)),
    [pathname],
  );

  const setVisiblePages = useCallback((pages: Set<number>) => {
    visiblePagesRef.current = pages;
    setVisiblePagesState(pages);
  }, []);

  const clearAllPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = undefined;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
  }, []);

  // --- 轮询 ---
  const pollVisiblePagesRef = useRef<() => Promise<void>>();

  pollVisiblePagesRef.current = async () => {
    const sorted = [...taskListRef.current].sort(sortCombinedByTimeDesc);
    const pagesToPoll = new Set<number>();

    visiblePagesRef.current.forEach((page) => {
      const start = (page - 1) * pageSize;
      const end = page * pageSize;
      sorted.slice(start, end).forEach((batch) => {
        if (!batchHasPendingSubTasks(batch)) return;
        const tid = (batch.taskId ?? batch.id) as number;
        const apiPage = batchPollPageRef.current.get(tid);
        if (apiPage) pagesToPoll.add(apiPage);
      });
    });

    if (pagesToPoll.size === 0) return;

    try {
      const responses = await Promise.all(
        [...pagesToPoll].map((p) => fetchTaskList(buildListRequestPayload(p))),
      );

      const allNewTasks: CombinedTaskItem[] = [];
      responses.forEach((response) => {
        if (response.success && response.data) {
          (response.data.creationDetailList || []).forEach((detail, i) => {
            allNewTasks.push(transformDetailToCombinedItem(detail, i));
          });
        }
      });

      const newTaskMap = new Map(
        allNewTasks.map((t) => [(t.taskId ?? t.id) as number, t]),
      );
      setTaskList((prev) => patchTasksAfterPoll(prev, newTaskMap));

      const hasAnyPending = allNewTasks.some((task) =>
        (task.rawData?.taskList || []).some((t) => t.state === TaskState.PENDING),
      );
      if (hasAnyPending) {
        if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = setTimeout(() => {
          pollVisiblePagesRef.current?.();
        }, batchPollingInterval);
      }
    } catch (error) {
      console.error('[TaskList] Error polling visible pages:', error);
      if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = setTimeout(() => {
        pollVisiblePagesRef.current?.();
      }, batchPollingInterval);
    }
  };

  const startPolling = useCallback(() => {
    clearAllPolling();
    pollingTimerRef.current = setTimeout(() => {
      pollVisiblePagesRef.current?.();
    }, batchPollingInterval);
  }, [clearAllPolling, batchPollingInterval]);

  // --- CRUD ---

  const loadTaskList = useCallback(
    async (page: number, append: boolean = false) => {
      try {
        if (!queryTypes.length || loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);

        const response = await fetchTaskList(buildListRequestPayload(page));

        if (response.success && response.data) {
          const detailList = response.data.creationDetailList || [];
          const combinedList: CombinedTaskItem[] = detailList.map((detail, index) =>
            transformDetailToCombinedItem(detail, (page - 1) * pageSize + index),
          );

          // 记录每个 batch 来自哪个 API 页
          combinedList.forEach((task, index) => {
            const globalIndex = append ? 0 : (page - 1) * pageSize;
            const apiPage = Math.floor((globalIndex + index) / pageSize) + 1;
            const tid = (task.taskId ?? task.id) as number;
            batchPollPageRef.current.set(tid, apiPage);
          });

          if (append) {
            setTaskList((prev) => {
              // 修正 append 模式下的 apiPage
              combinedList.forEach((task, index) => {
                const tid = (task.taskId ?? task.id) as number;
                batchPollPageRef.current.set(
                  tid,
                  Math.floor((prev.length + index) / pageSize) + 1,
                );
              });
              return [...prev, ...combinedList];
            });
          } else {
            setTaskList(combinedList);
          }

          setHasMore(detailList.length === pageSize);
        } else {
          setHasMore(false);
          if (!append) setTaskList([]);
        }
      } catch (error) {
        console.error('Failed to load task list:', error);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [buildListRequestPayload, transformDetailToCombinedItem],
  );

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const nextPage = pageNum + 1;
    setPageNum(nextPage);
    loadTaskList(nextPage, true);
  }, [loading, hasMore, pageNum, loadTaskList]);

  const refresh = useCallback(() => {
    if (loadingRef.current) {
      setLoading(false);
      return;
    }

    if (!active) {
      clearAllPolling();
      setLoading(false);
      return;
    }

    const isLoggedIn = UserStore.getState().getLoginState();
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    if (!queryTypes.length) {
      setLoading(false);
      return;
    }

    clearAllPolling();
    setTaskList([]);
    batchPollPageRef.current.clear();
    setPageNum(1);
    setHasMore(true);
    setVisiblePages(new Set([1]));
    loadTaskList(1, false);
  }, [active, clearAllPolling, loadTaskList, queryTypes.length]);

  const deleteTask = useCallback(async (taskId: number | string) => {
    try {
      const res = await deleteBatch({ parentId: taskId });
      if (res.code === 200) {
        message.success(t('merror.tip.deleteSuccess'));
        batchPollPageRef.current.delete(Number(taskId));
        setTaskList((prev) => prev.filter((t) => t.id !== taskId));
      } else {
        message.error(res.message || t('merror.tip.deleteError'));
      }
    } catch {
      message.error(t('merror.tip.deleteError'));
    }
  }, []);

  const deleteMediaItem = useCallback(
    (batchId: number | string, mediaItemId: number | string) => {
      setTaskList(
        (prev) =>
          prev
            .map((task) => {
              if (task.id !== batchId) return task;
              const updatedMediaItems = task.mediaItems.filter(
                (item) => item.id !== mediaItemId,
              );
              if (updatedMediaItems.length === 0) {
                batchPollPageRef.current.delete((task.taskId ?? task.id) as number);
                return null;
              }
              return { ...task, mediaItems: updatedMediaItems };
            })
            .filter(Boolean) as CombinedTaskItem[],
      );
    },
    [],
  );

  const updateMediaItemTitle = useCallback(
    (batchId: number | string, mediaItemId: number | string, newTitle: string) => {
      setTaskList((prev) =>
        prev.map((task) => {
          if (task.id !== batchId) return task;
          return {
            ...task,
            mediaItems: task.mediaItems.map((item) =>
              item.id === mediaItemId ? { ...item, title: newTitle } : item,
            ),
          };
        }),
      );
    },
    [],
  );
  // --- effects ---

  useEffect(() => {
    if (!active) {
      clearAllPolling();
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      clearAllPolling();
      startPolling();
    }, PAGE_SWITCH_DEBOUNCE);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [active, visiblePages, clearAllPolling, startPolling]);

  useEffect(() => {
    return () => clearAllPolling();
  }, [clearAllPolling]);

  const taskListForDisplay = useMemo(() => {
    return [...taskList].sort(sortCombinedByTimeDesc);
  }, [taskList]);

  // --- return ---

  return {
    taskList: taskListForDisplay,
    loading,
    hasMore,
    pageNum,
    loadMore,
    refresh,
    deleteTask,
    deleteMediaItem,
    updateMediaItemTitle,
    setVisiblePages,
  };
};
